import {
  type MaintenanceChecklistDefinition,
  type MaintenanceChecklistFieldType,
  type MaintenanceTemplateKind,
} from '@gb-ticket/shared-types';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChecklistPreview } from '@/products/maintenance/features/protocol-renderer/checklist-preview';
import { ChecklistFieldProperties } from '@/products/maintenance/features/templates/checklist-field-properties';
import {
  addField,
  addSection,
  duplicateField,
  moveField,
  moveSection,
  removeField,
  removeSection,
  updateField,
} from '@/products/maintenance/features/templates/checklist-editor-state';
import { useState } from 'react';

const FIELD_TYPES: MaintenanceChecklistFieldType[] = [
  'info',
  'boolean',
  'choice',
  'short_text',
  'long_text',
  'integer',
  'decimal',
  'measurement',
  'date',
  'time',
  'attachment',
];

type Props = {
  definition: MaintenanceChecklistDefinition;
  onChange: (next: MaintenanceChecklistDefinition) => void;
  kind: MaintenanceTemplateKind;
  pdfPageCount?: number | null;
  selectedFieldKey?: string | null;
  onSelectFieldKey?: (fieldKey: string | null) => void;
};

export function ChecklistEditor({
  definition,
  onChange,
  kind,
  pdfPageCount,
  selectedFieldKey: selectedFieldKeyProp,
  onSelectFieldKey,
}: Props) {
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [internalSelected, setInternalSelected] = useState<string | null>(null);
  const selectedFieldKey = selectedFieldKeyProp ?? internalSelected;
  const setSelectedFieldKey = onSelectFieldKey ?? setInternalSelected;

  function addFieldToSection(sectionId: string, type: MaintenanceChecklistFieldType) {
    let next = addField(definition, sectionId, type);
    if (kind === 'pdf_overlay') {
      const section = next.sections.find((s) => s.id === sectionId);
      const field = section?.fields[section.fields.length - 1];
      if (field) {
        next = updateField(next, sectionId, field.fieldKey, {
          placement: { page: 1, x: 0.1, y: 0.1, width: 0.35, height: 0.06 },
        });
        setSelectedFieldKey(field.fieldKey);
      }
    }
    onChange(next);
  }

  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div className="space-y-6">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!newSectionTitle.trim()) {
              return;
            }
            onChange(addSection(definition, newSectionTitle));
            setNewSectionTitle('');
          }}
        >
          <div className="min-w-[12rem] flex-1 space-y-1">
            <Label htmlFor="sec-title">Neuer Abschnitt</Label>
            <Input
              id="sec-title"
              value={newSectionTitle}
              onChange={(e) => setNewSectionTitle(e.target.value)}
            />
          </div>
          <Button type="submit" variant="secondary">
            Hinzufügen
          </Button>
        </form>

        {definition.sections.map((section, sectionIndex) => (
          <div key={section.id} className="space-y-3 rounded-md border p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-semibold">{section.title}</h3>
              <div className="flex flex-wrap gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={sectionIndex === 0}
                  onClick={() => onChange(moveSection(definition, section.id, 'up'))}
                >
                  ↑
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={sectionIndex === definition.sections.length - 1}
                  onClick={() => onChange(moveSection(definition, section.id, 'down'))}
                >
                  ↓
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onChange(removeSection(definition, section.id))}
                >
                  Abschnitt entfernen
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {FIELD_TYPES.map((type) => (
                <Button
                  key={type}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => addFieldToSection(section.id, type)}
                >
                  + {type}
                </Button>
              ))}
            </div>
            <ul className="space-y-2 text-sm">
              {section.fields.map((field, fieldIndex) => (
                <li
                  key={field.fieldKey}
                  className={`rounded border p-2 ${selectedFieldKey === field.fieldKey ? 'border-primary' : ''}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <button
                      type="button"
                      className="text-left font-medium hover:underline"
                      onClick={() => setSelectedFieldKey(field.fieldKey)}
                    >
                      {field.label} ({field.type})
                    </button>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={fieldIndex === 0}
                        onClick={() =>
                          onChange(moveField(definition, section.id, field.fieldKey, 'up'))
                        }
                      >
                        ↑
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={fieldIndex === section.fields.length - 1}
                        onClick={() =>
                          onChange(moveField(definition, section.id, field.fieldKey, 'down'))
                        }
                      >
                        ↓
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          onChange(duplicateField(definition, section.id, field.fieldKey))
                        }
                      >
                        Duplizieren
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          onChange(removeField(definition, section.id, field.fieldKey))
                        }
                      >
                        Entfernen
                      </Button>
                    </div>
                  </div>
                  {selectedFieldKey === field.fieldKey ? (
                    <ChecklistFieldProperties
                      definition={definition}
                      sectionId={section.id}
                      field={field}
                      kind={kind}
                      pdfPageCount={pdfPageCount}
                      onChange={onChange}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <h2 className="text-base font-semibold">Strukturierte Vorschau</h2>
        <ChecklistPreview definition={definition} />
      </div>
    </div>
  );
}
